import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { startOfDay, subMinutes, endOfDay } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import { task } from 'ember-concurrency';
import type GenericResourceModel from 'garaje/models/generic-resource';
import { createURN, getIdFromURN } from 'garaje/models/generic-resource';
import type MapFeatureModel from 'garaje/models/map-feature';
import type MapFloorModel from 'garaje/models/map-floor';
import type AjaxService from 'garaje/services/ajax';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LiveMapService from 'garaje/services/live-map';
import type { AvailabilityStatus, ResourceState, ResourceType } from 'garaje/services/live-map';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

interface ResourcePopupComponentArgs {
  selectedDate: Date;
  feature: MapFeatureModel;
  mapFloor: MapFloorModel;
  onCancel: () => void;
}

export interface CreateReservationPayload {
  resourceId: string;
  relationId: string;
  startTime: string;
  endTime: string;
  reservedFor: string;
  reservedBy: string;
  bookingSource: string;
}

export default class ResourcePopupComponent extends Component<ResourcePopupComponentArgs> {
  @service declare state: StateService;
  @service declare store: StoreService;
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare liveMap: LiveMapService;
  @service declare currentAdmin: CurrentAdminService;

  @tracked resource: GenericResourceModel | undefined;
  @tracked resourceState: ResourceState | undefined;
  @tracked resourceAvailability: AvailabilityStatus | undefined;

  constructor(owner: unknown, args: ResourcePopupComponentArgs) {
    super(owner, args);
    void this.loadResourceAndStateTask.perform();
  }

  loadResourceAndStateTask = task({ drop: true }, async (): Promise<void> => {
    const resourceId = this.args.feature.externalId;
    this.resource = await this.store.findRecord('generic-resource', resourceId);
    this.resourceState = this.liveMap.getResourceStateByIdAndType(resourceId, this.args.feature.type as ResourceType);
    this.resourceAvailability = this.resourceState?.status.availabilityStatus;
  });

  get hasAssignment(): boolean {
    return this.resourceState?.assignedTo != null;
  }

  get isAssignedToCurrentUser(): boolean {
    return getIdFromURN(this.resourceState?.assignedTo) === this.state.currentUser?.id;
  }

  get isReservedByCurrentUser(): boolean {
    return getIdFromURN(this.resourceState?.status.reservedFor) === this.state.currentUser?.id;
  }

  get resourceTypeLabel(): string {
    // TODO: we will need to fetch all the different resource types and their labels
    // once the API is ready
    switch (this.resource?.resourceTypeId) {
      case 2:
        return 'Parking';
      default:
        return 'Resource';
    }
  }

  createResourceReservationTask = task({ drop: true }, async () => {
    try {
      const currentEmployee = this.currentAdmin.employee;

      // The current employee viewing map can only book resources for themselves
      if (!currentEmployee) {
        throw new Error('Cannot find current employee');
      }

      if (!this.resource) {
        throw new Error('Cannot find resource to reserve');
      }

      const locationTime = this.state.getOfficeLocationTime(
        startOfDay(subMinutes(this.args.selectedDate, this.state.minutesBetweenTimezones(this.args.selectedDate))),
      );
      const locationEndTime = this.state.getOfficeLocationTime(
        endOfDay(toZonedTime(this.args.selectedDate, this.state.currentLocation.timezone)),
      );

      const payload: CreateReservationPayload = {
        resourceId: this.resource?.id,
        relationId: createURN('location', this.state.currentLocation.id),
        startTime: locationTime.toISOString(),
        endTime: locationEndTime.toISOString(),
        reservedFor: createURN('employee', currentEmployee?.id),
        reservedBy: createURN('employee', currentEmployee?.id),
        bookingSource: 'web_app',
      };

      await this.ajax.request(`/a/rms/v2/generics/reservations`, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
        data: { data: payload },
      });

      void this.liveMap.restartPollingLiveMap();
      void this.loadResourceAndStateTask.perform();
      this.flashMessages.showFlash('success', 'Reservation successfully created');
    } catch (error) {
      const errorText = parseErrorForDisplay(error);
      this.flashMessages.showFlash('error', errorText);
    }
  });

  releaseResourceReservationTask = task({ drop: true }, async () => {
    try {
      const reservationId = this.resourceState?.status.reservationId;
      if (!reservationId) throw new Error('Cannot find reservation id');

      await this.ajax.request(`/a/rms/v2/generics/reservations/${reservationId}/release`, {
        type: 'POST',
        contentType: 'application/vnd.api+json',
      });

      void this.liveMap.restartPollingLiveMap();
      void this.loadResourceAndStateTask.perform();
      this.flashMessages.showFlash('success', 'Reservation cancelled');
    } catch (error) {
      const errorText = parseErrorForDisplay(error);
      this.flashMessages.showFlash('error', errorText);
    }
  });
}
